import { type ImgHTMLAttributes, forwardRef, useState } from 'react';
import { User } from 'lucide-react';

export interface AvatarProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'size'> {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  name?: string;
}

const Avatar = forwardRef<HTMLImageElement, AvatarProps>(
  ({ size = 'md', name, src, alt, className = '', ...props }, ref) => {
    const [hasError, setHasError] = useState(false);

    const sizeStyles = {
      xs: 'w-6 h-6 text-xs',
      sm: 'w-8 h-8 text-sm',
      md: 'w-10 h-10 text-base',
      lg: 'w-14 h-14 text-lg',
      xl: 'w-20 h-20 text-xl',
    };

    const iconSizes = {
      xs: 'w-3 h-3',
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-7 h-7',
      xl: 'w-10 h-10',
    };

    const getInitials = (name: string) => {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    };

    const baseStyles = 'rounded-full flex items-center justify-center overflow-hidden flex-shrink-0';

    if (!src || hasError) {
      if (name) {
        return (
          <div
            className={`${baseStyles} ${sizeStyles[size]} bg-lavender-200 text-lavender-700 dark:bg-lavender-900/50 dark:text-lavender-400 font-semibold ${className}`}
          >
            {getInitials(name)}
          </div>
        );
      }

      return (
        <div
          className={`${baseStyles} ${sizeStyles[size]} bg-gray-200 text-gray-500 dark:bg-dark-700 dark:text-gray-400 ${className}`}
        >
          <User className={iconSizes[size]} />
        </div>
      );
    }

    return (
      <img
        ref={ref}
        src={src}
        alt={alt || name || 'Avatar'}
        className={`${baseStyles} ${sizeStyles[size]} object-cover ${className}`}
        onError={() => setHasError(true)}
        {...props}
      />
    );
  }
);

Avatar.displayName = 'Avatar';

export default Avatar;
